import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import { useAPIConfirmPltKS } from 'api/kinerja/useAPIKinerja'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

type StepConfirmationProps = {
  schools: Array<PelaksanaanSchool>
  onClose: () => void
  onPrev: () => void
  onSuccess: () => void
}

const CONFIRMATION_TEXT = 'Saya menyatakan bahwa data sudah sesuai dan benar.'

export function StepConfirmation({
  schools,
  onClose,
  onPrev,
  onSuccess,
}: StepConfirmationProps) {
  const toast = useToast()
  const formMethods = useForm()
  const confirmationMutation = useAPIConfirmPltKS()

  const errorMessage = formMethods.formState.errors?.confirmationText?.message
  const isEmpty = !formMethods.formState.isDirty

  const schoolsToConfirm = schools.filter(
    (school) => school.confirmationStatus === 'CONFIRMED'
  )
  const schoolsToReject = schools.filter(
    (school) => school.confirmationStatus === 'REJECTED'
  )

  const handleSubmit = () => {
    confirmationMutation.mutate(
      {
        schools: schools.map((school) => ({
          npsn: school.npsn,
          confirmationStatus: school.confirmationStatus,
        })),
      },
      {
        onSuccess,
        onError: () => {
          onClose()

          toast({ message: 'Gagal konfirmasi data. Coba lagi.' })
        },
      }
    )
  }

  return (
    <form
      onSubmit={formMethods.handleSubmit(handleSubmit)}
      className="space-y-4"
    >
      <div className="space-y-1">
        <Text variant="display-sm">
          Pastikan sesuai karena tidak dapat diubah lagi
        </Text>
        <Text color="subdued">
          Untuk mengubah data, Anda hanya dapat melakukannya di Dapodik. Cek
          ulang atau konfirmasi jika sudah sesuai.
        </Text>
      </div>

      <div className="space-y-4 overflow-wrap-anywhere">
        {schoolsToConfirm.length > 0 && (
          <div className="space-y-3 rounded bg-green-0 p-4 ">
            <div className="flex space-x-2 items-center">
              <Icon>check_circle_outline</Icon>
              <Text variant="body-bold">Akses Anda dibuka ke:</Text>
            </div>
            <ul className="space-y-1">
              {schoolsToConfirm.map((school) => (
                <Text as="li" variant="body-sm" key={school.npsn}>
                  {school.schoolName}
                </Text>
              ))}
            </ul>
          </div>
        )}

        {schoolsToReject.length > 0 && (
          <div className="space-y-3 rounded bg-red-5 p-4 overflow-">
            <div className="flex space-x-2 items-center">
              <Icon>highlight_off</Icon>
              <Text variant="body-bold">Akses Anda dihapus dari:</Text>
            </div>
            <ul className="space-y-1">
              {schoolsToReject.map((school) => (
                <Text as="li" variant="body-sm" key={school.npsn}>
                  {school.schoolName}
                </Text>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <Text variant="body-lg" color="subdued">
          Mohon ketik kalimat ini ke kolom kalimat persetujuan:
        </Text>

        <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
          <Text variant="heading-sm" color="inherit">
            {CONFIRMATION_TEXT}
          </Text>
        </div>

        <div className="space-y-1 max-w-xs">
          <Text as="label" htmlFor="confirmation-text">
            Kalimat persetujuan
          </Text>
          <Textarea
            isInvalid={!!errorMessage}
            errorMessage={errorMessage}
            id="confirmation-text"
            placeholder="Ketik di sini."
            rows={3}
            {...formMethods.register('confirmationText', {
              validate: (value) =>
                value === CONFIRMATION_TEXT || 'Mohon cek ejaan dan coba lagi.',
            })}
          />
        </div>
      </div>

      <div className="flex space-x-3 !justify-start">
        <Button color="white" size="lg" onClick={onPrev}>
          Kembali
        </Button>
        <Button
          color="blue"
          size="lg"
          disabled={confirmationMutation.isLoading || isEmpty}
          isLoading={confirmationMutation.isLoading}
          type="submit"
        >
          Setuju
        </Button>
      </div>
    </form>
  )
}
